<script setup lang="ts">
import { computed, onMounted, ref, watch, onBeforeMount } from "vue";
import { availabilityColor } from "../availabilityColor";
import ImportScreenSelectionDialog from "./ImportScreenSelectionDialog.vue";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const props = defineProps<{
	flight: FlightDO;
	availableScreens: ScreenDO[];
	excludedScreens: ScreenDO[];
	loading: boolean;
}>();

const search = ref("");
const showExcluded = ref(false);
const showImportDialog = ref(false);

const allScreens = computed(() => [
	...props.availableScreens,
	...props.excludedScreens,
]);

const couldIds = computed<number[]>({
	get: () =>
		(props.flight.refined_screen_selection_could as number[] | null) ?? [],
	set: (val) => (props.flight.refined_screen_selection_could = val),
});

const mustIds = computed<number[]>({
	get: () =>
		(props.flight.refined_screen_selection_must as number[] | null) ?? [],
	set: (val) => (props.flight.refined_screen_selection_must = val),
});

const initSelections = () => {
	if (!props.flight.refined_screen_selection_must) {
		mustIds.value = allScreens.value
			.filter((s) => s.is_mandatory_must)
			.map((s) => s.id);
	}
	if (!props.flight.refined_screen_selection_could) {
		const mustSet = new Set(mustIds.value);
		couldIds.value = props.availableScreens
			.filter((s) => !mustSet.has(s.id) && isScreenAvailableForSelection(s))
			.map((s) => s.id);
	}
};

onBeforeMount(() => {
	props.flight.refined_screen_selection_could = null;
	props.flight.refined_screen_selection_must = null;
});

onMounted(initSelections);

watch(
	() => props.availableScreens,
	(screens) => {
		if (screens.length === 0) return;
		props.flight.refined_screen_selection_could = null;
		props.flight.refined_screen_selection_must = null;
		initSelections();
	}
);

const isInCould = (id: number) => couldIds.value.includes(id);
const isInMust = (id: number) => mustIds.value.includes(id);

const toggleCould = (screen: ScreenDO) => {
	if (screen.is_mandatory_must) return;
	if (isInCould(screen.id)) {
		couldIds.value = couldIds.value.filter((id) => id !== screen.id);
	} else {
		couldIds.value = [...couldIds.value, screen.id];
		mustIds.value = mustIds.value.filter((id) => id !== screen.id);
	}
};

const toggleMust = (screen: ScreenDO) => {
	if (screen.is_mandatory_must) return;
	if (isInMust(screen.id)) {
		mustIds.value = mustIds.value.filter((id) => id !== screen.id);
	} else {
		mustIds.value = [...mustIds.value, screen.id];
		couldIds.value = couldIds.value.filter((id) => id !== screen.id);
	}
};

const selectAll = () => {
	const mandatoryMustIds = allScreens.value
		.filter((s) => s.is_mandatory_must)
		.map((s) => s.id);
	const mandatorySet = new Set(mandatoryMustIds);
	couldIds.value = props.availableScreens
		.filter((s) => !mandatorySet.has(s.id) && isScreenAvailableForSelection(s))
		.map((s) => s.id);
	mustIds.value = mandatoryMustIds;
};

const deselectAll = () => {
	mustIds.value = allScreens.value
		.filter((s) => s.is_mandatory_must)
		.map((s) => s.id);
	couldIds.value = [];
};

const visibleScreens = computed(() => {
	const lc = search.value.toLowerCase();
	const selectedIds = new Set([...couldIds.value, ...mustIds.value]);

	const pool = showExcluded.value
		? allScreens.value
		: allScreens.value.filter((s) => selectedIds.has(s.id));

	return pool.filter(
		(s) =>
			!lc ||
			(s.name ?? "").toLowerCase().includes(lc) ||
			(s.reference_id ?? "").toLowerCase().includes(lc)
	);
});

const selectedCount = computed(
	() => couldIds.value.length + mustIds.value.length
);

const importedScreens = ref<ScreenDO[] | null>(null);

const onImported = (screens: ScreenDO[]) => {
	importedScreens.value = screens;
};

const applyImportOnly = () => {
	const importedIds = new Set((importedScreens.value ?? []).map((s) => s.id));
	const mandatoryMustIds = allScreens.value
		.filter((s) => s.is_mandatory_must)
		.map((s) => s.id);
	const mandatorySet = new Set(mandatoryMustIds);
	mustIds.value = mandatoryMustIds;
	couldIds.value = allScreens.value
		.filter(
			(s) =>
				!mandatorySet.has(s.id) &&
				importedIds.has(s.id) &&
				isScreenAvailableForSelection(s)
		)
		.map((s) => s.id);
	importedScreens.value = null;
};

const applyFilterByImport = () => {
	const importedIds = new Set((importedScreens.value ?? []).map((s) => s.id));
	mustIds.value = mustIds.value.filter(
		(id) =>
			importedIds.has(id) ||
			allScreens.value.find((s) => s.id === id)?.is_mandatory_must
	);
	couldIds.value = couldIds.value.filter((id) => importedIds.has(id));
	importedScreens.value = null;
};

const isScreenAvailableForSelection = (screen: ScreenDO) => {
	if (screen.is_mandatory_must) return false;
	if (screen.availability?.fill_rate == null) return false;
	return screen.inventory_type === "digital"
		? screen.availability.fill_rate < 1 && screen.availability.availability > 0
		: screen.availability.fill_rate === 0.0 &&
				screen.availability.availability > 0;
};

const headers = [
	{ title: "Screen", key: "name", align: "start" as const, sortable: true },
	{ title: "Format", key: "size", align: "start" as const, sortable: true },
	{
		title: "Fill rate",
		key: "availability.fill_rate",
		align: "start" as const,
		sortable: true,
		width: 180,
	},
	{
		title: "Could",
		key: "could",
		align: "center" as const,
		sortable: false,
		width: 72,
	},
	{
		title: "Must",
		key: "must",
		align: "center" as const,
		sortable: false,
		width: 72,
	},
];
</script>

<template>
	<v-card class="pa-5">
		<div
			v-if="loading"
			class="d-flex align-center justify-center py-8 gap-2 text-medium-emphasis text-caption"
		>
			<v-progress-circular size="20" width="2" indeterminate />
			Loading available screens…
		</div>

		<template v-else>
			<!-- Toolbar -->
			<div class="d-flex align-center gap-3 mb-3 mt-1">
				<v-text-field
					v-model="search"
					prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
					placeholder="Filter screens…"
					variant="filled"
					density="compact"
					hide-details
					clearable
					class="flex-grow-1"
				/>
				<div class="d-flex align-center mx-10">
					<span class="text-body-2 mr-5">Show filtered screens</span>
					<v-switch
						v-model="showExcluded"
						color="primary"
						density="compact"
						hide-details
					/>
				</div>
				<v-menu>
					<template v-slot:activator="{ props: menuProps }">
						<v-btn
							icon="fa: fa-regular fa-ellipsis-vertical"
							variant="text"
							size="small"
							v-bind="menuProps"
						/>
					</template>
					<v-list density="compact">
						<v-list-item title="Select all" @click="selectAll" />
						<v-list-item title="De-select all" @click="deselectAll" />
						<v-list-item
							title="Select from file"
							@click="showImportDialog = true"
						/>
					</v-list>
				</v-menu>

				<import-screen-selection-dialog
					v-model="showImportDialog"
					:flight="flight"
					@imported="onImported"
				/>
			</div>

			<v-sheet
				v-if="importedScreens !== null"
				border
				rounded
				class="import-action-block mb-3 pa-4"
			>
				<div class="d-flex align-center gap-2 mb-3">
					<span class="text-body-2 font-weight-medium">
						Screens with availability returned from file:
						<strong>{{ importedScreens.length }}</strong>
					</span>
					<v-btn
						icon
						variant="text"
						size="x-small"
						class="ml-auto"
						@click="importedScreens = null"
					>
						<v-icon size="14">fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</div>
				<div class="d-flex">
					<v-btn
						variant="tonal"
						color="primary"
						size="small"
						class="mr-5"
						prepend-icon="fa: fa-regular fa-check-double"
						@click="applyImportOnly"
					>
						Select only these screens as could selection
					</v-btn>
					<v-btn
						variant="tonal"
						color="primary"
						size="small"
						class=""
						prepend-icon="fa: fa-regular fa-filter"
						@click="applyFilterByImport"
					>
						Filter current selection based on the uploaded file
					</v-btn>
				</div>
			</v-sheet>

			<v-alert
				v-if="selectedCount === 0"
				color="warning"
				:icon="false"
				class="mb-3 text-caption"
				><span style="font-size: 12px">
					Select at least one screen to continue.</span
				>
			</v-alert>

			<v-data-table
				:headers="headers"
				:items="visibleScreens"
				:items-per-page="15"
				density="compact"
				class="elevation-0 screen-table"
			>
				<template v-slot:item.name="{ item }">
					<v-tooltip
						:text="`${item.reference_id} - ${item.name}`"
						location="top"
						:disabled="!item.reference_id"
					>
						<template v-slot:activator="{ props: ttProps }">
							<span v-bind="ttProps" class="screen-name">{{
								item.name ?? "—"
							}}</span>
						</template>
					</v-tooltip>
				</template>

				<template v-slot:item.size="{ item }">
					<span class="text-medium-emphasis text-caption">
						{{ item.size?.label ?? "—" }}
					</span>
				</template>

				<template v-slot:item.availability.fill_rate="{ item }">
					<div
						v-if="
							item.availability?.fill_rate != null &&
							item.availability?.fill_rate < 1 &&
							item.availability?.availability > 0
						"
						class="fill-rate-cell"
					>
						<div class="fill-rate-cell__bar">
							<div
								class="fill-rate-cell__booked"
								:style="{
									width: (item.availability.fill_rate * 100).toFixed(1) + '%',
									background: availabilityColor(
										(1 - item.availability.fill_rate) * 100
									),
								}"
							/>
							<div
								class="fill-rate-cell__remaining"
								:style="{
									width: ((1 - item.availability.fill_rate) * 100).toFixed(1) + '%',
								}"
							/>
						</div>
						<span class="fill-rate-cell__label">
							{{ (item.availability.fill_rate * 100).toFixed(1) }}%
						</span>
					</div>
					<span v-else class="text-medium-emphasis text-caption">—</span>
				</template>

				<template v-slot:item.could="{ item }">
					<v-checkbox
						:model-value="isInCould(item.id)"
						:disabled="!isScreenAvailableForSelection(item)"
						color="primary"
						hide-details
						density="compact"
						class="d-flex justify-center"
						@update:model-value="toggleCould(item)"
					/>
				</template>

				<template v-slot:item.must="{ item }">
					<v-checkbox
						:model-value="isInMust(item.id)"
						:disabled="!isScreenAvailableForSelection(item)"
						color="primary"
						hide-details
						density="compact"
						class="d-flex justify-center"
						@update:model-value="toggleMust(item)"
					/>
				</template>

				<template v-slot:no-data>
					<div class="py-6 text-center text-caption text-medium-emphasis">
						{{
							search
								? "No screens match your search"
								: "No screens available for the current filter configuration"
						}}
					</div>
				</template>
			</v-data-table>
		</template>
	</v-card>
</template>

<style scoped lang="scss">
.import-action-block {
	background: rgba(var(--v-theme-primary), 0.04);
}

.screen-table {
	:deep(th) {
		font-size: 0.75rem !important;
	}
}

.screen-name {
	display: inline-block;
	max-width: 25ch;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.fill-rate-cell {
	display: flex;
	align-items: center;
	gap: 6px;

	&__bar {
		display: flex;
		width: 52px;
		height: 6px;
		border-radius: 3px;
		overflow: hidden;
		flex-shrink: 0;
	}

	&__booked,
	&__remaining {
		height: 100%;
		transition: width 0.2s ease;
	}

	&__remaining {
		background: rgba(0, 0, 0, 0.08);
	}

	&__label {
		font-size: 0.7rem;
		font-weight: 600;
		white-space: nowrap;
	}
}
</style>
